import { useFetchAllSalesRegions } from "@/features/search-filter/hooks/FilterFetchers/UseFetchAllSalesRegion";
import {
  stenaSailingLocation,
  Spinner,
  stenaCalendar,
  stenaCheckCircle,
  stenaSearch,
  stenaUserGroupLarge,
  stenaUserMultiple,
} from "@stenajs-webui/elements";
import { useContext, useEffect, useState } from "react";
import { FilterSection } from "../../../../components/search-filter/FilterSection";
import { FilterCheckbox } from "@stenajs-webui/filter";
import { useFetchAllSellers } from "@/features/search-filter/hooks/FilterFetchers/UseFetchAllSellers";
import { MultiSelect, Select } from "@stenajs-webui/select";
import { SellerOption } from "@/features/search-filter/components/sections/SellerFilterSection";
import { Filter } from "../../../../components/search-filter/Filter";
import { SingleValue } from "react-select";
import { DateRangeDualTextInput } from "@stenajs-webui/calendar";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { useLoadCustomers } from "@/features/search-filter/hooks/UseLoadCustomers";
import { OptionItem } from "@/types/OptionItem";
import { RebatesFilterChips } from "./RebatesFilterChips";
import {
  getChipsFromFilterState,
  RebatesFilterContext,
  sectionIdConstants,
} from "./RebatesFilterContext";
import { RebatesFilterContextType } from "../../types/types";
import { useDefaultYearSelection } from "@/hooks/UseDefaultYearSelection";
import { Seller } from "@/features/search-filter/types/FilterEntitys";
import { RebateGoalStatus } from "../../types/types";
import { NumericTextInput } from "@/components/numeric-text-input/NumericTextInput";

const terms = [
  { id: "YEARLY", label: "Yearly" },
  { id: "HALF_YEAR", label: "Half year" },
  { id: "MONTHLY", label: "Monthly" },
  { id: "QUARTERLY", label: "Quarterly" },
  { id: "USER_DEF", label: "Custom" },
];
const goalStatusOptions: { value: RebateGoalStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "FAILED", label: "Failed" },
  { value: "NEGOTIATED", label: "Negotiated" },
  { value: "ONGOING", label: "Ongoing" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "REACHED", label: "Reached" },
  { value: "VOID", label: "Void" },
];

export const SalesRegionsFilterSection = () => {
  const { salesRegions: items, loading } = useFetchAllSalesRegions();
  const { filterState, dispatch, openSectionIds, toggleSectionId } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const sectionId = sectionIdConstants.salesRegions;
  return (
    <FilterSection
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
      leftIcon={stenaSailingLocation}
      label={"Sales regions"}
    >
      {loading ? (
        <Spinner />
      ) : (
        items.map((item) => (
          <FilterCheckbox
            key={item.id}
            label={item.name}
            value={
              !!filterState.selectedSalesRegions.find(
                (salesRegion) => salesRegion.id === item.id
              )
            }
            onValueChange={() =>
              dispatch({ type: "TOGGLE_SALES_REGION", payload: item })
            }
          />
        ))
      )}
    </FilterSection>
  );
};

export const TermFilterSection = () => {
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const { selectedTerms } = filterState;
  const sectionId = sectionIdConstants.terms;
  return (
    <FilterSection
      leftIcon={stenaCalendar}
      label={"Term"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      {terms.map((item) => (
        <FilterCheckbox
          key={item.id}
          label={item.label}
          value={!!selectedTerms.find((term) => term.id === item.id)}
          onValueChange={() => dispatch({ type: "TOGGLE_TERM", payload: item })}
        />
      ))}
    </FilterSection>
  );
};

export const ValidityDatesSection = () => {
  const { filterState, dispatch, openSectionIds, toggleSectionId } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const sectionId = sectionIdConstants.validity;
  return (
    <FilterSection
      leftIcon={stenaCalendar}
      label={"Validity dates"}
      isOpen={openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <DateRangeDualTextInput
        appendTo={document.body}
        zIndex={ZIndex.highest}
        value={filterState.selectedValidity}
        onValueChange={(selectedValidity) =>
          dispatch({ type: "SET_VALIDITY", payload: selectedValidity })
        }
      />
    </FilterSection>
  );
};

const transformToSellerOptions = (seller: Seller) => ({
  value: seller.id,
  label: seller.name,
});

export const YearSection = () => {
  const { availableYears, loading } = useDefaultYearSelection();
  const { filterState, dispatch, openSectionIds, toggleSectionId } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const sectionId = sectionIdConstants.year;

  const yearOptions =
    availableYears?.map(({ year }) => ({
      value: year,
      label: year,
    })) ?? [];

  return (
    <FilterSection
      leftIcon={stenaCalendar}
      label={"Year"}
      isOpen={openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <Select
        isClearable
        options={yearOptions}
        isLoading={loading}
        value={filterState.selectedYear}
        onChange={(selectedYear) =>
          dispatch({ type: "SET_YEAR", payload: selectedYear ?? undefined })
        }
      />
    </FilterSection>
  );
};

export const SellerSection = () => {
  const { sellers: allSellers } = useFetchAllSellers();
  const { filterState, dispatch, openSectionIds, toggleSectionId } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const sectionId = sectionIdConstants.seller;

  const sellerOptions = allSellers.map(transformToSellerOptions);

  return (
    <FilterSection
      leftIcon={stenaUserGroupLarge}
      label={"Seller"}
      isOpen={openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <Select
        isClearable
        options={sellerOptions}
        value={filterState.selectedSeller}
        onChange={(newValue: SingleValue<SellerOption>) =>
          dispatch({
            type: "SET_SELLER",
            payload: newValue as SellerOption | undefined,
          })
        }
      />
    </FilterSection>
  );
};

export const BookingCustomerSection = () => {
  const { fetchCustomers, loading, customers } = useLoadCustomers();
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState<OptionItem[]>([]);

  useEffect(() => {
    if (customers) {
      setOptions(
        customers.map((customer) => ({
          value: customer.id,
          label: customer.custIndex,
        }))
      );
    }
  }, [customers]);

  useEffect(() => {
    if (searchValue.length > 2) {
      fetchCustomers(searchValue);
    }
  }, [fetchCustomers, searchValue]);

  const sectionId = sectionIdConstants.customer;
  return (
    <FilterSection
      leftIcon={stenaUserMultiple}
      label={"Booking customer"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <Select
        options={options ?? []}
        isLoading={loading}
        value={filterState.selectedCustomer}
        isClearable
        inputValue={searchValue}
        onInputChange={setSearchValue}
        onChange={(value) => dispatch({ type: "SET_CUSTOMER", payload: value })}
        onMenuClose={() => setOptions([])}
      />
    </FilterSection>
  );
};

export const AgreementNumberSection = ({
  onSearch,
}: {
  onSearch: () => void;
}) => {
  const { filterState, dispatch, openSectionIds, toggleSectionId } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const sectionId = sectionIdConstants.agreementNumber;

  return (
    <FilterSection
      leftIcon={stenaSearch}
      label={"Agreement no."}
      isOpen={openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <NumericTextInput
        maxDecimals={0}
        stripLeadingZeros
        placeholder={"Agreement number"}
        value={filterState.selectedAgreementNumber}
        onValueChange={(selectedAgreementNumber) =>
          dispatch({
            type: "SET_AGREEMENT_NUMBER",
            payload: selectedAgreementNumber,
          })
        }
        onEnter={onSearch}
      />
    </FilterSection>
  );
};

export const GoalStatusSection = () => {
  const { filterState, dispatch, openSectionIds, toggleSectionId } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;
  const sectionId = sectionIdConstants.goalStatus;

  const selectedOptions = goalStatusOptions.filter((opt) =>
    filterState.selectedGoalStatusCodes.includes(opt.value)
  );

  return (
    <FilterSection
      leftIcon={stenaCheckCircle}
      label={"Goal status"}
      isOpen={openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <MultiSelect
        isClearable
        options={goalStatusOptions}
        value={selectedOptions}
        onChange={(newValue) =>
          dispatch({
            type: "SET_GOAL_STATUS",
            payload: newValue.map((opt) => opt.value),
          })
        }
      />
    </FilterSection>
  );
};

export const RebatesFilter = ({
  onSearch,
  showGoalStatus = false,
  showAgreementNumber = false,
}: {
  onSearch: () => void;
  showGoalStatus?: boolean;
  showAgreementNumber?: boolean;
}) => {
  const {
    clearFilter,
    filterState,
    setSelectedFilterChips,
    filterIsOpen,
    setFilterIsOpen,
  } = useContext(RebatesFilterContext) as RebatesFilterContextType;

  const searchHandler = () => {
    setSelectedFilterChips(getChipsFromFilterState(filterState));
    onSearch();
    setFilterIsOpen(false);
  };

  return (
    <Filter
      filterIsOpen={filterIsOpen}
      setFilterIsOpen={setFilterIsOpen}
      clearFilter={clearFilter}
      onSearch={searchHandler}
      filterChips={<RebatesFilterChips />}
    >
      <YearSection />
      {showAgreementNumber && (
        <AgreementNumberSection onSearch={searchHandler} />
      )}
      <ValidityDatesSection />
      <BookingCustomerSection />
      <SellerSection />
      <SalesRegionsFilterSection />
      <TermFilterSection />
      {showGoalStatus && <GoalStatusSection />}
    </Filter>
  );
};
